import { useState, useEffect, useCallback } from "react";
import ProcessoSincronizacaoService from "../../services/resource/ProcessoSincronizacaoService";

const PROCESSO_LABELS = {
    ENTREGA_VOLUME_SAP: {
        label: "Entrega Volume",
        sub: "SAP",
        icon: "fas fa-truck-loading",
        group: "SAP",
    },
    RETORNO_VOLUME_SAP: {
        label: "Retorno Volume",
        sub: "SAP",
        icon: "fas fa-undo-alt",
        group: "SAP",
    },
    ARQUIVO_GREENMILE: {
        label: "Greenmile",
        sub: "Greenmile",
        icon: "fas fa-file-import",
        group: "Logística",
    },
    ENTREGA_CAXIAS_BI: {
        label: "Entrega Caxias",
        sub: "BI",
        icon: "fas fa-chart-bar",
        group: "BI",
    },
    DESPESA_FORTESAC: {
        label: "Despesa Logística",
        sub: "Fortesac",
        icon: "fas fa-receipt",
        group: "Fortesac",
    },
    DESPESA_BI: {
        label: "Despesa Distribuidores",
        sub: "BI",
        icon: "fas fa-chart-pie",
        group: "BI",
    },
    RECEITA_BI: {
        label: "Receita",
        sub: "BI",
        icon: "fas fa-chart-line",
        group: "BI",
    },
    RECEITA_FORTESAC: {
        label: "Receita",
        sub: "Fortesac",
        icon: "fas fa-coins",
        group: "Fortesac",
    },
    EASYPALLET: {
        label: "EasyPallet",
        sub: "Integração",
        icon: "fas fa-pallet",
        group: "Logística",
    },
    TRIXLOG: {
        label: "Trixlog",
        sub: "Telemetria",
        icon: "fas fa-satellite-dish",
        group: "Logística",
    },
    FUNCIONARIO_FORTESAC: {
        label: "Funcionários",
        sub: "Fortesac",
        icon: "fas fa-users",
        group: "Fortesac",
    },
    LANCAMENTO_DESPESA_FLUIG: {
        label: "Lanç. Despesa",
        sub: "Fluig",
        icon: "fas fa-file-invoice",
        group: "Fluig",
    },
    INFRACOES_CONCEPT: {
        label: "Infrações",
        sub: "Concept",
        icon: "fas fa-exclamation-triangle",
        group: "Logística",
    },
    JORNADA_TRABALHADA_SOLIDES: {
        label: "Jornada de Trabalho",
        sub: "Solides",
        icon: "fas fa-history",
        group: "RH",
    },
    ESCALA_ROTAS: {
        label: "Escala de Rotas",
        sub: "Importação",
        icon: "fas fa-route",
        group: "Logística",
    },
    FOLHA_PAGAMENTO: {
        label: "Folha de Pagamento - Horas Extras",
        sub: "RH",
        icon: "fas fa-list",
        group: "Logística",
    },
};

function formatDate(date) {
    if (!date) return "—";

    return new Date(date).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function formatDuration(seconds) {
    if (seconds == null) return "—";

    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes}m ${secs}s`;
}

function timeAgo(date) {
    if (!date) return "—";

    const diff = Math.floor((Date.now() - new Date(date)) / 1000);

    if (diff < 60) return `há ${diff}s`;
    if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;

    return `há ${Math.floor(diff / 86400)}d`;
}

function getStatus(item) {
    if (!item.fim && item.inicio) {
        return {
            key: "running",
            label: "Em execução",
            badge: "bg-blue-50 text-blue-700",
            icon: "bg-blue-50 text-blue-600",
            dot: "bg-blue-500",
        };
    }

    if (item.erro) {
        return {
            key: "error",
            label: "Erro",
            badge: "bg-red-50 text-red-700",
            icon: "bg-red-50 text-red-600",
            dot: "bg-red-500",
        };
    }

    if (item.fim) {
        return {
            key: "ok",
            label: "Sucesso",
            badge: "bg-emerald-50 text-emerald-700",
            icon: "bg-emerald-50 text-emerald-600",
            dot: "bg-emerald-500",
        };
    }

    return {
        key: "never",
        label: "Nunca",
        badge: "bg-muted text-muted-foreground",
        icon: "bg-muted text-muted-foreground",
        dot: "bg-muted-foreground",
    };
}

function parseErrors(raw) {
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed : [String(parsed)];
    } catch {
        return [raw];
    }
}

export default function SincronizacaoMonitor() {
    const service = new ProcessoSincronizacaoService();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("ALL");
    const [expanded, setExpanded] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [, forceUpdate] = useState(0);

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const response = await service.listarUltimasSincronizacao();

            setData(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setLastRefresh(new Date());
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const interval = setInterval(() => {
            forceUpdate((n) => n + 1);
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const filteredData = data.filter((item) => {
        const status = getStatus(item).key;

        if (filter === "ALL") return true;
        if (filter === "OK") return status === "ok";
        if (filter === "ERROR") return status === "error";
        if (filter === "RUNNING") return status === "running";

        return true;
    });

    const counts = {
        ok: data.filter((i) => getStatus(i).key === "ok").length,
        error: data.filter((i) => getStatus(i).key === "error").length,
        running: data.filter((i) => getStatus(i).key === "running").length,
    };

    const filters = [
        {
            key: "ALL",
            label: "Todos",
            count: data.length,
            active: "bg-foreground text-background border-foreground",
        },
        {
            key: "OK",
            label: "Sucesso",
            count: counts.ok,
            active: "bg-emerald-100 text-emerald-700 border-emerald-300",
        },
        {
            key: "ERROR",
            label: "Erros",
            count: counts.error,
            active: "bg-red-100 text-red-700 border-red-300",
        },
        {
            key: "RUNNING",
            label: "Executando",
            count: counts.running,
            active: "bg-blue-100 text-blue-700 border-blue-300",
        },
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* HEADER */}
            <div className="border-b border-border bg-card">
                <div className="mx-auto flex flex-col gap-5 px-6 py-6 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                            Monitoramento de Sincronizações
                        </span>

                        <p className="mt-1 text-sm text-muted-foreground">
                            Última execução dos processos integrados
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {lastRefresh && (
                            <span className="text-sm text-muted-foreground">
                                Atualizado às{" "}
                                {lastRefresh.toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                })}
                            </span>
                        )}

                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-primary/40 hover:text-primary disabled:opacity-50"
                        >
                            <i
                                className={`fas fa-sync-alt ${loading ? "animate-spin" : ""
                                    }`}
                            />
                            Atualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* FILTERS */}
            <div className="mx-auto flex flex-wrap gap-3 px-6 py-3">
                {filters.map((item) => (
                    <button
                        key={item.key}
                        onClick={() => setFilter(item.key)}
                        className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition ${filter === item.key
                            ? item.active
                            : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary"
                            }`}
                    >
                        {item.label}

                        <span className="rounded-full bg-black/10 px-2 py-0.5 text-xs font-semibold">
                            {item.count}
                        </span>
                    </button>
                ))}
            </div>

            {/* GRID */}
            <div className="mx-auto px-6 pb-10">
                {loading ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 9 }).map((_, index) => (
                            <div
                                key={index}
                                className="animate-pulse rounded-2xl border border-border bg-card p-5"
                            >
                                <div className="mb-4 h-5 w-1/2 rounded bg-muted" />

                                <div className="space-y-3">
                                    <div className="h-4 rounded bg-muted" />
                                    <div className="h-4 w-2/3 rounded bg-muted" />
                                    <div className="h-4 w-1/3 rounded bg-muted" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card py-20 text-center">
                        <i className="fas fa-box-open mb-4 text-4xl text-muted-foreground" />

                        <h3 className="text-lg font-semibold text-muted-foreground">
                            Nenhum processo encontrado
                        </h3>

                        <p className="mt-1 text-sm text-muted-foreground">
                            Não existem sincronizações nesta categoria.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {filteredData.map((item, index) => (
                            <SyncCard
                                key={`${item.processo}-${index}`}
                                item={item}
                                expanded={expanded === item.processo}
                                onToggleExpand={() =>
                                    setExpanded(
                                        expanded === item.processo ? null : item.processo
                                    )
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function SyncCard({ item, expanded, onToggleExpand }) {

    const meta = PROCESSO_LABELS[item.processo] || {
        label: item.processo,
        sub: "",
        icon: "fas fa-cog",
        group: "Sistema",
    };

    const status = getStatus(item);

    const errors = parseErrors(item.mensagemErro);

    return (
        <div className={`flex h-full flex-col overflow-hidden rounded-2xl transition hover:-translate-y-1 hover:shadow-pop bg-card  ${item.duracaoSuperiorDoisDias === true ? 'border-[3px] border-destructive ' : 'border-border'}`}>
            <div className="flex items-start justify-between gap-4 p-4">
                <div className="flex min-w-0 gap-3">
                    <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl text-lg ${status.icon}`}
                    >
                        <i className={meta.icon} />
                    </div>

                    <div className="min-w-0">
                        <h3 className="truncate text-sm font-bold text-foreground">
                            {meta.label}
                        </h3>

                        <p className="mt-1 text-xs text-muted-foreground">
                            {meta.sub} • {meta.group}
                        </p>
                    </div>
                </div>

                <div
                    className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${status.badge}`}
                >
                    <span
                        className={`h-2 w-2 rounded-full ${status.dot} ${status.key === "running" ? "animate-pulse" : ""
                            }`}
                    />

                    {status.label}
                </div>
            </div>

            {/* BODY */}
            <div className="grid grid-cols-2 gap-3 px-5 pb-3">
                <Info label="Início" value={formatDate(item.inicio)} />
                <Info label="Fim" value={formatDate(item.fim)} />
                <Info label="Há Quanto Tempo" value={timeAgo(item.fim || item.inicio)} />
                <Info
                    label="Duração"
                    value={formatDuration(item.duracaoSegundos)}
                />
            </div>

            {/* FILE */}
            {item.nomeArquivo && (
                <div className="mx-5 mb-4 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm text-primary">
                    <i className="fas fa-paperclip" />
                    <span className="truncate">{item.nomeArquivo}</span>
                </div>
            )}

            {/* ERRORS */}
            {item.erro && item.mensagemErro && (
                <div className="px-5 pb-3">
                    <button
                        onClick={onToggleExpand}
                        className="flex w-full items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-left text-sm font-medium text-red-700 transition hover:bg-red-100"
                    >
                        <i className="fas fa-exclamation-circle" />

                        {expanded ? "Ocultar erros" : "Ver erros"}

                        <span className="ml-auto rounded-full bg-red-200 px-2 py-0.5 text-xs font-bold text-red-800">
                            {errors.length}
                        </span>

                        <i
                            className={`fas ${expanded ? "fa-chevron-up" : "fa-chevron-down"
                                } text-xs`}
                        />
                    </button>

                    {expanded && (
                        <div className="mt-3 overflow-hidden rounded-xl border border-red-200">
                            {errors.map((error, index) => (
                                <div
                                    key={index}
                                    className="flex gap-3 border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800 last:border-none"
                                >
                                    <span className="font-bold text-red-400">
                                        {index + 1}.
                                    </span>

                                    <span className="break-words text-xs">
                                        {error}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* FOOTER */}
            <div className="mt-auto flex items-center justify-between border-t border-border bg-muted px-5 py-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <i
                        className={
                            item.sincronizacao === "AUTO"
                                ? "fas fa-robot"
                                : "fas fa-hand-pointer"
                        }
                    />

                    {item.sincronizacao === "AUTO"
                        ? "Automático"
                        : "Manual"}
                </div>

                <div className="text-xs text-muted-foreground">
                    {item.duracaoSegundos != null &&
                        `${formatDuration(item.duracaoSegundos)} total`}
                </div>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </span>

            <p className="mt-1 break-words text-xs text-foreground">
                {value}
            </p>
        </div>
    );
}